import { useState, useRef, useCallback } from "react";
import { X, Loader2, ImagePlus, AlertCircle, Star, RotateCw, GripVertical } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "./ui/button";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortableImage = ({ id, url, index, totalImages, onRemove, onSetCover, onRotate, getImageUrl }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-xl overflow-hidden bg-stone-100 group border-2 transition-all ${
        index === 0 ? "border-emerald-500 ring-2 ring-emerald-200" : "border-stone-200 hover:border-stone-300"
      } ${isDragging ? "shadow-2xl scale-105" : ""}`}
    >
      <img
        src={getImageUrl(url)}
        alt={`Imagine ${index + 1}`}
        className="w-full h-full object-cover"
      />

      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 w-8 h-8 bg-black/60 hover:bg-black/80 rounded-lg flex items-center justify-center cursor-grab active:cursor-grabbing transition-colors"
      >
        <GripVertical className="w-4 h-4 text-white" />
      </div>

      {/* Cover badge */}
      {index === 0 && (
        <div className="absolute top-2 left-12 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
          <Star className="w-3 h-3 fill-current" />
          Coperta
        </div>
      )}

      {/* Position number */}
      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
        {index + 1}
      </div>

      {/* Controls overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {/* Rotate button */}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={(e) => { e.stopPropagation(); onRotate(index); }}
              className="w-8 h-8 p-0 bg-white/90 hover:bg-white"
              title="Roteste 90°"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
            {index !== 0 && (
              <Button
                type="button"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onSetCover(index); }}
                className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
              >
                <Star className="w-3 h-3 mr-1" />
                Coperta
              </Button>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            className="w-8 h-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImageUploaderModern = ({ images = [], onImagesChange, maxImages = 15 }) => {
  const [uploading, setUploading] = useState(false);
  const [rotating, setRotating] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("/api")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maxim ${maxImages} imagini permise`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    const validFiles = filesToUpload.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name}: nu este o imagine`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: prea mare (max 5MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newImages = response.data.uploaded.map((img) => img.url);
      onImagesChange([...images, ...newImages]);

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach((err) => toast.error(err));
      }

      if (newImages.length > 0) {
        toast.success(`${newImages.length} imagini incarcate`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Eroare la incarcare");
    } finally {
      setUploading(false);
    }
  }, [images, maxImages, onImagesChange]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.indexOf(active.id);
      const newIndex = images.indexOf(over.id);
      onImagesChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const setAsCover = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    const [removed] = newImages.splice(index, 1);
    newImages.unshift(removed);
    onImagesChange(newImages);
    toast.success("Imagine setata ca si coperta");
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
    toast.success("Imagine eliminata");
  };

  const rotateImage = async (index) => {
    const imgUrl = getImageUrl(images[index]);
    setRotating(index);
    
    try {
      // Load image, rotate on canvas, re-upload
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imgUrl;
      });
      
      const canvas = document.createElement("canvas");
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext("2d");
      
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9)
      );
      
      const formData = new FormData();
      formData.append("files", blob, "rotated.jpg");
      
      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (response.data.uploaded?.length > 0) {
        const newUrl = response.data.uploaded[0].url;
        const newImages = [...images];
        newImages[index] = newUrl;
        onImagesChange(newImages);
        toast.success("Imagine rotita cu succes");
      }
    } catch (error) {
      console.error("Rotate error:", error);
      toast.error("Eroare la rotirea imaginii");
    } finally {
      setRotating(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      {images.length > 1 && (
        <div className="flex items-center gap-2 text-sm text-stone-500 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
          <GripVertical className="w-4 h-4 text-amber-500" />
          <span>Trage imaginile pentru a le reordona. Prima imagine va fi coperta anuntului.</span>
        </div>
      )}

      {/* Image Grid with DnD */}
      {images.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map((img, index) => (
                <div key={img} className="relative">
                  {rotating === index && (
                    <div className="absolute inset-0 z-50 bg-white/80 rounded-xl flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                    </div>
                  )}
                  <SortableImage
                    id={img}
                    url={img}
                    index={index}
                    totalImages={images.length}
                    onRemove={removeImage}
                    onSetCover={setAsCover}
                    onRotate={rotateImage}
                    getImageUrl={getImageUrl}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          data-testid="image-upload-dropzone"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${
              dragActive
                ? "border-emerald-500 bg-emerald-50"
                : "border-stone-300 hover:border-emerald-400 hover:bg-stone-50"
            }
            ${uploading ? "pointer-events-none opacity-50" : ""}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleInputChange}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-3" />
              <p className="text-stone-600">Se incarca...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <ImagePlus className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-stone-700 font-medium mb-1">
                {dragActive ? "Elibereaza pentru a incarca" : "Adauga fotografii"}
              </p>
              <p className="text-stone-500 text-sm">
                Trage imagini aici sau click pentru a selecta
              </p>
              <p className="text-stone-400 text-xs mt-2">
                JPG, PNG, WebP - Max 5MB per imagine - {images.length}/{maxImages}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Max reached warning */}
      {images.length >= maxImages && (
        <div className="flex items-center gap-2 text-amber-700 text-sm bg-amber-50 border border-amber-200 px-4 py-3 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>Ai atins limita de {maxImages} imagini. Elimina imagini pentru a adauga altele noi.</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploaderModern;
